import { DAYS_OF_WEEK } from "./types/foyer";
import { minutesOfDay, zonedTimeToUtc, localDayInZone } from "./signage";
export function isOpenOn(hours, day, minutes) {
    if (!hours)
        return false;
    const today = hours[day];
    if (today === null || today === void 0 ? void 0 : today.allDay)
        return true;
    const yesterday = hours[previousDay(day)];
    if (yesterday && !yesterday.closed && !yesterday.allDay) {
        const opens = minutesOfDay(yesterday.opens);
        const closes = minutesOfDay(yesterday.closes);
        if (closes < opens && minutes < closes)
            return true;
    }
    if (!today || today.closed)
        return false;
    const opens = minutesOfDay(today.opens);
    const closes = minutesOfDay(today.closes);
    if (closes === opens)
        return false;
    if (closes < opens)
        return minutes >= opens;
    return minutes >= opens && minutes < closes;
}
function previousDay(day) {
    const i = DAYS_OF_WEEK.indexOf(day);
    return DAYS_OF_WEEK[(i + DAYS_OF_WEEK.length - 1) % DAYS_OF_WEEK.length];
}
function shiftYmd(ymd, days) {
    const [y, m, d] = ymd.split("-").map(Number);
    const dt = new Date(Date.UTC(y, (m !== null && m !== void 0 ? m : 1) - 1, (d !== null && d !== void 0 ? d : 1) + days));
    return dt.toISOString().slice(0, 10);
}
const JS_DAY_ORDER = [
    "sun",
    "mon",
    "tue",
    "wed",
    "thu",
    "fri",
    "sat",
];
function dayOfWeekFor(ymd) {
    const [y, m, d] = ymd.split("-").map(Number);
    return JS_DAY_ORDER[new Date(Date.UTC(y, (m !== null && m !== void 0 ? m : 1) - 1, d !== null && d !== void 0 ? d : 1)).getUTCDay()];
}
export function openIntervalsInRange(hours, timeZone, from, to) {
    if (!hours)
        return [];
    const fromMs = from.getTime();
    const toMs = to.getTime();
    if (toMs <= fromMs)
        return [];
    let ymd = shiftYmd(localDayInZone(timeZone, from).ymd, -1);
    const lastYmd = localDayInZone(timeZone, new Date(toMs - 1)).ymd;
    const out = [];
    for (let guard = 0; guard < 10000 && ymd <= lastYmd; guard++, ymd = shiftYmd(ymd, 1)) {
        const day = hours[dayOfWeekFor(ymd)];
        if (!day || day.closed)
            continue;
        let startMin;
        let endYmd;
        let endMin;
        if (day.allDay) {
            startMin = 0;
            endYmd = shiftYmd(ymd, 1);
            endMin = 0;
        }
        else {
            const opens = minutesOfDay(day.opens);
            const closes = minutesOfDay(day.closes);
            if (closes === opens)
                continue;
            startMin = opens;
            if (closes > opens) {
                endYmd = ymd;
                endMin = closes;
            }
            else {
                endYmd = shiftYmd(ymd, 1);
                endMin = closes;
            }
        }
        const sessionStart = zonedTimeToUtc(timeZone, ymd, startMin).getTime();
        const sessionEnd = zonedTimeToUtc(timeZone, endYmd, endMin).getTime();
        const overlapStart = Math.max(sessionStart, fromMs);
        const overlapEnd = Math.min(sessionEnd, toMs);
        if (overlapEnd <= overlapStart)
            continue;
        const last = out[out.length - 1];
        if (last && overlapStart <= last[1]) {
            last[1] = Math.max(last[1], overlapEnd);
        }
        else {
            out.push([overlapStart, overlapEnd]);
        }
    }
    return out;
}
export function openMinutesInRange(hours, timeZone, from, to) {
    let totalMs = 0;
    for (const [a, b] of openIntervalsInRange(hours, timeZone, from, to)) {
        totalMs += b - a;
    }
    return totalMs / 60000;
}
export function weeklyOpenMinutes(hours) {
    if (!hours)
        return 0;
    let total = 0;
    for (const day of DAYS_OF_WEEK) {
        const d = hours[day];
        if (!d || d.closed)
            continue;
        if (d.allDay) {
            total += 1440;
            continue;
        }
        const opens = minutesOfDay(d.opens);
        const closes = minutesOfDay(d.closes);
        if (closes === opens)
            continue;
        total += closes > opens ? closes - opens : 1440 - opens + closes;
    }
    return total;
}
export const MAX_IMPRESSION_MULTIPLIER = 250;
export function impressionMultiplier(input) {
    const { hours, audience, panelCount, day, minutes } = input;
    const weekly = weeklyOpenMinutes(hours);
    if (!hours || weekly <= 0)
        return { gap: "no_opening_hours" };
    if (!(audience === null || audience === void 0 ? void 0 : audience.entriesPerWeek) || audience.entriesPerWeek <= 0) {
        return { gap: "no_entries_per_week" };
    }
    if (!audience.dwellMinutes || audience.dwellMinutes <= 0) {
        return { gap: "no_dwell_minutes" };
    }
    if (!panelCount || panelCount <= 0)
        return { gap: "no_panel_count" };
    if (!isOpenOn(hours, day, minutes))
        return { gap: "closed_now" };
    const averageHourlyGuests = audience.entriesPerWeek * Math.min(audience.dwellMinutes / weekly, 1);
    const raw = Math.ceil(averageHourlyGuests * panelCount);
    return { multiplier: Math.min(raw, MAX_IMPRESSION_MULTIPLIER) };
}
export function describeMultiplierGap(gap) {
    switch (gap) {
        case "no_opening_hours":
            return "Opening hours are not set for this premise";
        case "no_entries_per_week":
            return "Visitors per week is not set for this premise";
        case "no_dwell_minutes":
            return "Average visit length is not set for this premise";
        case "no_panel_count":
            return "This screen does not say how many panels it drives";
        case "closed_now":
            return "The premise is closed right now";
    }
}
